import type { QueryStatus } from '@tanstack/react-query';

import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { isAllRoomsName } from '../../shared/roomNameMatcher';
import { parseDealOptionNumber } from '../dealOptions/dealOptionsFormatters';
import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import { type EgsApplyBlocker, isGateBlocker } from './egsBlockers';
import { getRatePlanName } from './egsRatePlans';
import { EGS_SLOTS, getGuestSlot, hasArchiveDisagreement, isArchivedRow } from './egsUtils';
import {
  EGS_SELL_SOURCE_CURRENCY,
  getEgsContractedCurrency,
  getSlotCostAmount,
  getSlotSellAmount,
} from './egsWriteCurrency';
import type { EgsGuestSlot, EgsRoomGroup } from './types';

export type { EgsApplyBlocker };

const FREE_OF_CHARGE = 'free of charge';
const PAID_DIRECTLY_TO_HOTEL = 'paid directly to the hotel';

export type EgsSlotOrigin = 'free_of_charge' | 'kept' | 'salesforce';

export interface EgsSlotPlan {
  cost: { after: number; before: number | null };
  origin: EgsSlotOrigin;
  sell: { after: number; before: number | null };
}

export type EgsArchiveDecision = 'archived' | 'live';

export interface EgsGateResolution {
  /** Which side of a `payable_at_property_conflict` the single stored flag should take. */
  payableAtProperty?: boolean;
  /** The Salesforce row that wins a contested guest type, by row id. */
  slotWinnerRowIds: Partial<Record<EgsGuestSlot, string>>;
}

export const EMPTY_GATE_RESOLUTION: EgsGateResolution = { slotWinnerRowIds: {} };

export interface EgsSlotConflictOption {
  collectedBy: string;
  cost: number | undefined;
  roomLabel: string;
  rowId: string;
  sell: number | undefined;
}

export interface EgsSlotConflict {
  options: Array<EgsSlotConflictOption>;
  resolvedRowId?: string;
  slot: EgsGuestSlot;
}

export interface EgsArchiveDisagreement {
  decision?: EgsArchiveDecision;
  name: string;
  rowId: string;
}

// One question for both causes, since a room rate stores one `payable_at_property` flag for all four slots.
export interface EgsPayableConflict {
  affectsKeptCharges: boolean;
  resolvedTo?: boolean;
  rowsDisagree: boolean;
}

// Answered gates stay listed, so the CM can see what they chose and change it.
export interface EgsPlanGates {
  archiveDisagreements: Array<EgsArchiveDisagreement>;
  payableConflict?: EgsPayableConflict;
  slotConflicts: Array<EgsSlotConflict>;
}

export interface RoomRateEgsPlan {
  blocker?: EgsApplyBlocker;
  /** Only where the blocker is slot-level, so a record-wide refusal cannot name a guest type it does not mean. */
  blockerSlots?: Array<EgsGuestSlot>;
  existingCurrency?: string;
  existingRecordId?: string;
  gates: EgsPlanGates;
  /** False when the merged record is identical to what is already stored, so there is nothing to send. */
  hasChange: boolean;
  /** True when Salesforce says every slot is free, where creating an all-zero record is worse than none. */
  isAllFreeOfCharge: boolean;
  operation: 'create' | 'update';
  payableAtProperty: boolean;
  ratePlanName: string;
  /** Only the Salesforce records that put a row into this write, never one that lost every slot. */
  roomLabels: Array<string>;
  roomRateId: string;
  roomTypeId: string;
  roomTypeName: string;
  slots: Record<EgsGuestSlot, EgsSlotPlan>;
  /** Guest types Salesforce priced at nothing while charging for them, so the write left them alone. */
  unpricedSlots: Array<EgsGuestSlot>;
  writeCurrency: string;
}

// Everything the write reads, so two rows sharing a fingerprint are duplicates. Cost must come from the field
// the write reads: `converted_cost__c` has no decimals, so two contracted costs share it and order would win.
function getRowFingerprint(row: OpportunityRelatedExtraGuestSurcharge, contractedCurrency: string | undefined) {
  return JSON.stringify([
    (row.collected_by__c ?? '').trim().toLowerCase(),
    getSlotCostAmount({ contractedCurrency, row }) ?? null,
    parseDealOptionNumber(row.sell_price_aud__c) ?? null,
  ]);
}

// A decision replaces the signal for that row only; rows nobody was asked about keep reading the flag.
function isRowArchived(row: OpportunityRelatedExtraGuestSurcharge, archiveByRowId: Record<string, EgsArchiveDecision>) {
  const decision = archiveByRowId[row.id];

  return decision ? decision === 'archived' : isArchivedRow(row);
}

/**
 * A record naming this room outranks a blanket "All Rooms" one, which is the default it was written to
 * override. At equal specificity, rows that agree merge and rows that disagree block.
 */
function resolveLiveRowsBySlot({
  archiveByRowId,
  contractedCurrency,
  groups,
  resolution,
}: {
  archiveByRowId: Record<string, EgsArchiveDecision>;
  contractedCurrency: string | undefined;
  groups: Array<EgsRoomGroup>;
  resolution: EgsGateResolution;
}) {
  const candidatesBySlot = new Map<
    EgsGuestSlot,
    Array<{ isAllRooms: boolean; roomLabel: string; row: OpportunityRelatedExtraGuestSurcharge }>
  >();

  for (const group of groups) {
    const isAllRooms = isAllRoomsName(group.roomLabel);

    for (const row of group.rows) {
      const slot = getGuestSlot(row.extra_guest_type__c);

      if (slot && !isRowArchived(row, archiveByRowId)) {
        candidatesBySlot.set(slot, [
          ...(candidatesBySlot.get(slot) ?? []),
          { isAllRooms, roomLabel: group.roomLabel, row },
        ]);
      }
    }
  }

  const conflictingSlots: Array<EgsGuestSlot> = [];
  const contributingLabels = new Set<string>();
  const rowsBySlot = new Map<EgsGuestSlot, OpportunityRelatedExtraGuestSurcharge>();
  const slotConflicts: Array<EgsSlotConflict> = [];

  // Slot order rather than insertion order, so the reported conflicts read consistently.
  for (const slot of EGS_SLOTS) {
    const candidates = candidatesBySlot.get(slot) ?? [];

    if (candidates.length === 0) {
      continue;
    }

    const specific = candidates.filter((candidate) => !candidate.isAllRooms);
    const winners = specific.length > 0 ? specific : candidates;

    const byFingerprint = new Map<string, (typeof winners)[number]>();
    winners.forEach((candidate) => {
      const fingerprint = getRowFingerprint(candidate.row, contractedCurrency);

      if (!byFingerprint.has(fingerprint)) {
        byFingerprint.set(fingerprint, candidate);
      }
    });

    if (byFingerprint.size > 1) {
      const options = Array.from(byFingerprint.values());
      const picked = options.find((candidate) => candidate.row.id === resolution.slotWinnerRowIds[slot]);

      slotConflicts.push({
        options: options.map(({ roomLabel, row }) => ({
          collectedBy: (row.collected_by__c ?? '').trim(),
          cost: getSlotCostAmount({ contractedCurrency, row }),
          roomLabel,
          rowId: row.id,
          sell: parseDealOptionNumber(row.sell_price_aud__c),
        })),
        resolvedRowId: picked?.row.id,
        slot,
      });

      if (!picked) {
        conflictingSlots.push(slot);
        winners.forEach((candidate) => contributingLabels.add(candidate.roomLabel));
        continue;
      }

      rowsBySlot.set(slot, picked.row);
      contributingLabels.add(picked.roomLabel);
      continue;
    }

    rowsBySlot.set(slot, winners[0].row);
    contributingLabels.add(winners[0].roomLabel);
  }

  return { conflictingSlots, contributingLabels, rowsBySlot, slotConflicts };
}

function isFreeOfCharge(row: OpportunityRelatedExtraGuestSurcharge) {
  return (row.collected_by__c ?? '').trim().toLowerCase() === FREE_OF_CHARGE;
}

/**
 * Free of Charge rows collect nothing and are excluded, so a free infant beside a paid-at-property adult is
 * not a conflict.
 */
function resolvePayableAtProperty(rows: Array<OpportunityRelatedExtraGuestSurcharge>, override: boolean | undefined) {
  const flags = new Set(
    rows
      .filter((row) => !isFreeOfCharge(row))
      .map((row) => (row.collected_by__c ?? '').trim().toLowerCase() === PAID_DIRECTLY_TO_HOTEL),
  );
  const isContested = flags.size > 1;

  // Only applied where the rows disagree, so a stale answer cannot flip an uncontested record.
  if (isContested && override !== undefined) {
    return { hasConflict: false, isContested, payableAtProperty: override };
  }

  return { hasConflict: isContested, isContested, payableAtProperty: flags.has(true) };
}

/** Build each slot and its validation issues from the same source amounts. */
function buildEgsSlots({
  contractedCurrency,
  existing,
  fxRateLookup,
  rowsBySlot,
  writeCurrency,
}: {
  contractedCurrency: string | undefined;
  existing: App.ExtraGuestSurcharges | undefined;
  fxRateLookup: Record<string, number>;
  rowsBySlot: Map<EgsGuestSlot, OpportunityRelatedExtraGuestSurcharge>;
  writeCurrency: string;
}) {
  const missingCostSlots: Array<EgsGuestSlot> = [];
  const slots = {} as Record<EgsGuestSlot, EgsSlotPlan>;
  const unconvertibleSlots: Array<EgsGuestSlot> = [];
  const unpricedSlots: Array<EgsGuestSlot> = [];

  for (const slot of EGS_SLOTS) {
    let row = rowsBySlot.get(slot);
    let costAmount: number | undefined;
    let sellAmount: number | undefined;

    if (row && !isFreeOfCharge(row)) {
      if ((parseDealOptionNumber(row.sell_price_aud__c) ?? 0) <= 0) {
        // Silence, not a zero price: keeps the stored charge, and origin stays `kept` for the currency guard.
        unpricedSlots.push(slot);
        row = undefined;
      } else {
        costAmount = getSlotCostAmount({ contractedCurrency, row });
        sellAmount = getSlotSellAmount({ rateLookup: fxRateLookup, row, writeCurrency });

        // Zero is a valid cost; an absent cost beside a real sell would overstate margin.
        if (costAmount === undefined) {
          missingCostSlots.push(slot);
        }

        if (sellAmount === undefined) {
          unconvertibleSlots.push(slot);
        }
      }
    }

    slots[slot] = buildSlotPlan({ costAmount, existing, row, sellAmount, slot });
  }

  return { missingCostSlots, slots, unconvertibleSlots, unpricedSlots };
}

function buildSlotPlan({
  costAmount,
  existing,
  row,
  sellAmount,
  slot,
}: {
  costAmount: number | undefined;
  existing: App.ExtraGuestSurcharges | undefined;
  row: OpportunityRelatedExtraGuestSurcharge | undefined;
  sellAmount: number | undefined;
  slot: EgsGuestSlot;
}): EgsSlotPlan {
  const beforeSell = existing ? existing[`${slot}_amount`] : null;
  const beforeCost = existing ? existing[`${slot}_cost`] : null;

  if (!row) {
    return {
      cost: { after: beforeCost ?? 0, before: beforeCost },
      origin: 'kept',
      sell: { after: beforeSell ?? 0, before: beforeSell },
    };
  }

  if (isFreeOfCharge(row)) {
    return { cost: { after: 0, before: beforeCost }, origin: 'free_of_charge', sell: { after: 0, before: beforeSell } };
  }

  return {
    cost: { after: costAmount ?? 0, before: beforeCost },
    origin: 'salesforce',
    sell: { after: sellAmount ?? 0, before: beforeSell },
  };
}

// Absent compares as zero, so an all-zero plan is not a change. Cost counts too, since it moves margin.
export function isSlotChanged(slotPlan: EgsSlotPlan) {
  return slotPlan.sell.after !== (slotPlan.sell.before ?? 0) || slotPlan.cost.after !== (slotPlan.cost.before ?? 0);
}

// Currency and `payable_at_property` are record-wide, so they reinterpret a kept charge without touching it.
function hasKeptCharge(slots: Record<EgsGuestSlot, EgsSlotPlan>) {
  return EGS_SLOTS.some(
    (slot) => slots[slot].origin === 'kept' && (slots[slot].sell.after > 0 || slots[slot].cost.after > 0),
  );
}

const FX_BLOCKER_BY_STATUS: Record<QueryStatus, EgsApplyBlocker> = {
  error: 'fx_rates_unavailable',
  pending: 'fx_rates_loading',
  success: 'missing_fx_rate',
};

/** The blocker, plus the guest types that caused it where the cause is slot-level rather than record-wide. */
interface EgsResolvedBlock {
  blocker?: EgsApplyBlocker;
  blockerSlots?: Array<EgsGuestSlot>;
}

function resolveBlocker({
  conflictingSlots,
  existingRecords,
  fxRatesStatus,
  hasPayableConflict,
  hasUndecidedArchiveRow,
  hasUnresolvedPayableMismatch,
  missingCostSlots,
  slots,
  unconvertibleSlots,
  writeCurrency,
}: {
  conflictingSlots: Array<EgsGuestSlot>;
  existingRecords: Array<App.ExtraGuestSurcharges>;
  fxRatesStatus: QueryStatus;
  hasPayableConflict: boolean;
  hasUndecidedArchiveRow: boolean;
  hasUnresolvedPayableMismatch: boolean;
  missingCostSlots: Array<EgsGuestSlot>;
  slots: Record<EgsGuestSlot, EgsSlotPlan>;
  unconvertibleSlots: Array<EgsGuestSlot>;
  writeCurrency: string;
}): EgsResolvedBlock {
  // Ahead of the slot conflict, which a disagreeing row usually causes; the root cause is the useful one.
  if (hasUndecidedArchiveRow) {
    return { blocker: 'archive_disagreement' };
  }

  if (conflictingSlots.length > 0) {
    return { blocker: 'conflicting_slots' };
  }

  // Pricing reads the first record per room rate and the query has no ORDER BY, so merging into one of
  // two would be a guess about which is live.
  if (existingRecords.length > 1) {
    return { blocker: 'multiple_existing_records' };
  }

  if (hasPayableConflict) {
    return { blocker: 'payable_at_property_conflict' };
  }

  const [existing] = existingRecords;

  // The record's currency governs sell as well as cost, so merging one currency into a record stored in
  // another reprices the slots being left alone. No answer makes this safe, unlike the payable flag below.
  if (existing && existing.currency !== writeCurrency && hasKeptCharge(slots)) {
    return { blocker: 'currency_mismatch' };
  }

  // A failed conversion blocks the write; query status distinguishes waiting, fetch failure, and an absent rate.
  if (unconvertibleSlots.length > 0) {
    return { blocker: FX_BLOCKER_BY_STATUS[fxRatesStatus], blockerSlots: unconvertibleSlots };
  }

  if (hasUnresolvedPayableMismatch) {
    return { blocker: 'payable_at_property_mismatch' };
  }

  // Every offending slot, not the first: fixing one and being refused again for the next reads as no progress.
  if (missingCostSlots.length > 0) {
    return { blocker: 'missing_cost_price', blockerSlots: missingCostSlots };
  }

  return {};
}

/**
 * One stored record per room rate holds all four guest slots in a single currency while Salesforce stores one
 * row per guest type, so this is a read-modify-write, and that single currency is why the sell is converted.
 */
export function buildRoomRateEgsPlan({
  archiveByRowId = {},
  egsByRoomRateId,
  fxRateLookup = {},
  fxRatesStatus = 'success',
  groups,
  resolution = EMPTY_GATE_RESOLUTION,
  selection,
}: {
  archiveByRowId?: Record<string, EgsArchiveDecision>;
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  fxRateLookup?: Record<string, number>;
  fxRatesStatus?: QueryStatus;
  groups: Array<EgsRoomGroup>;
  resolution?: EgsGateResolution;
  selection: RoomRateSelection;
}): RoomRateEgsPlan {
  const roomRateId = String(selection.roomRate.id);
  const existingRecords = egsByRoomRateId[roomRateId] ?? [];
  const [existing] = existingRecords;
  // Pooled, since a per-record view cannot see a guest type that two records both price.
  const rows = groups.flatMap((group) => group.rows);
  // Off every row, not the resolved ones: they all carry the parent opportunity's currency, and the
  // fingerprinting below needs it before any row has won a slot.
  const contractedCurrency = getEgsContractedCurrency(rows);
  const {
    conflictingSlots,
    contributingLabels,
    rowsBySlot: liveRowsBySlot,
    slotConflicts,
  } = resolveLiveRowsBySlot({ archiveByRowId, contractedCurrency, groups, resolution });
  const liveRows = Array.from(liveRowsBySlot.values());
  const {
    hasConflict,
    isContested,
    payableAtProperty: fromSalesforce,
  } = resolvePayableAtProperty(liveRows, resolution.payableAtProperty);

  const archiveDisagreements = rows.filter(hasArchiveDisagreement).map((row) => ({
    decision: archiveByRowId[row.id],
    name: row.name ?? '',
    rowId: row.id,
  }));

  const writeCurrency = contractedCurrency ?? EGS_SELL_SOURCE_CURRENCY;
  const { slots, unpricedSlots, missingCostSlots, unconvertibleSlots } = buildEgsSlots({
    contractedCurrency,
    existing,
    fxRateLookup,
    rowsBySlot: liveRowsBySlot,
    writeCurrency,
  });

  // Record-wide like the currency guard, but both answers are real values, so it is asked not refused.
  const isPayableMismatched = !!existing && !!existing.payable_at_property !== fromSalesforce && hasKeptCharge(slots);
  const payableAtProperty = isPayableMismatched ? (resolution.payableAtProperty ?? fromSalesforce) : fromSalesforce;

  const { blocker, blockerSlots } = resolveBlocker({
    conflictingSlots,
    existingRecords,
    fxRatesStatus,
    hasPayableConflict: hasConflict,
    hasUndecidedArchiveRow: archiveDisagreements.some((disagreement) => !disagreement.decision),
    hasUnresolvedPayableMismatch: isPayableMismatched && resolution.payableAtProperty === undefined,
    missingCostSlots,
    slots,
    unconvertibleSlots,
    writeCurrency,
  });

  return {
    blocker,
    blockerSlots,
    existingCurrency: existing?.currency,
    existingRecordId: existing?.id,
    gates: {
      archiveDisagreements,
      payableConflict:
        isContested || isPayableMismatched
          ? {
              affectsKeptCharges: isPayableMismatched,
              resolvedTo: resolution.payableAtProperty,
              rowsDisagree: isContested,
            }
          : undefined,
      slotConflicts,
    },
    hasChange: EGS_SLOTS.some((slot) => isSlotChanged(slots[slot])),
    isAllFreeOfCharge: liveRows.length > 0 && liveRows.every(isFreeOfCharge),
    operation: existing ? 'update' : 'create',
    payableAtProperty,
    ratePlanName: getRatePlanName(selection),
    unpricedSlots,
    roomLabels: groups.map((group) => group.roomLabel).filter((roomLabel) => contributingLabels.has(roomLabel)),
    roomRateId,
    roomTypeId: String(selection.roomType.id),
    roomTypeName: selection.roomType.name,
    slots,
    writeCurrency,
  };
}

/**
 * Keyed by room rate rather than by record: two records can target the same one, and iterating records first
 * would send both as independent writes where the later wins.
 */
export function buildEgsApplyPlans({
  archiveByRowId = {},
  egsByRoomRateId,
  fxRateLookup = {},
  fxRatesStatus = 'success',
  gateByRoomRateId = {},
  roomGroups,
  selectionsByRoomLabel,
}: {
  archiveByRowId?: Record<string, EgsArchiveDecision>;
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  fxRateLookup?: Record<string, number>;
  fxRatesStatus?: QueryStatus;
  gateByRoomRateId?: Record<string, EgsGateResolution>;
  roomGroups: Array<EgsRoomGroup>;
  selectionsByRoomLabel: Record<string, Array<RoomRateSelection>>;
}): Array<RoomRateEgsPlan> {
  const byRoomRateId = new Map<string, { groups: Array<EgsRoomGroup>; selection: RoomRateSelection }>();

  for (const group of roomGroups) {
    for (const selection of selectionsByRoomLabel[group.roomLabel] ?? []) {
      const roomRateId = String(selection.roomRate.id);
      const entry = byRoomRateId.get(roomRateId);

      if (entry) {
        entry.groups.push(group);
      } else {
        byRoomRateId.set(roomRateId, { groups: [group], selection });
      }
    }
  }

  return Array.from(byRoomRateId.values(), ({ groups, selection }) =>
    buildRoomRateEgsPlan({
      archiveByRowId,
      egsByRoomRateId,
      fxRateLookup,
      fxRatesStatus,
      groups,
      resolution: gateByRoomRateId[String(selection.roomRate.id)] ?? EMPTY_GATE_RESOLUTION,
      selection,
    }),
  );
}

/**
 * `hasChange` treating absent as zero excludes an all-zero create: `check_in_surcharge_applies` is an OR over
 * the surcharge types, so a record of zeros paints a surcharge marker across the date pickers for nothing.
 */
export function isApplicable(plan: RoomRateEgsPlan) {
  return !plan.blocker && plan.hasChange;
}

// Blocked only until the CM answers, unlike the refusals no answer can clear.
export function isWaitingOnGate(plan: RoomRateEgsPlan) {
  return !!plan.blocker && isGateBlocker(plan.blocker);
}

// Blocked only until the rates land, so counting it with the refusals promises the CM a fix that has nothing to do.
export function isWaitingOnFxRates(plan: RoomRateEgsPlan) {
  return plan.blocker === 'fx_rates_loading';
}

// Derive the alert from plans: a failed refresh can retain usable cached rates, and AUD needs no conversion.
export function getEgsFxRatesBlocker(plans: Array<RoomRateEgsPlan>) {
  return plans.find((plan) => plan.blocker === 'fx_rates_loading' || plan.blocker === 'fx_rates_unavailable')?.blocker;
}

export type EgsNothingToApplyReason = 'all_free_of_charge' | 'already_matches';

/**
 * Answered at full reach: narrowing the selection can only move a slot back to what is stored, so nothing
 * changing at full reach means nothing changes at any lesser reach. A blocked plan still counts as writable,
 * since dropping its conflict may unblock it.
 */
export function getNothingToApplyReason(plansAtFullReach: Array<RoomRateEgsPlan>): EgsNothingToApplyReason | undefined {
  if (plansAtFullReach.length === 0) {
    return undefined;
  }

  if (plansAtFullReach.some((plan) => isApplicable(plan) || !!plan.blocker)) {
    return undefined;
  }

  return plansAtFullReach.every((plan) => plan.isAllFreeOfCharge) ? 'all_free_of_charge' : 'already_matches';
}

export function buildEgsPayload(plan: RoomRateEgsPlan): App.ExtraGuestSurcharges {
  return {
    adult_amount: plan.slots.adult.sell.after,
    adult_cost: plan.slots.adult.cost.after,
    child_amount: plan.slots.child.sell.after,
    child_cost: plan.slots.child.cost.after,
    currency: plan.writeCurrency,
    id: plan.existingRecordId,
    infant_amount: plan.slots.infant.sell.after,
    infant_cost: plan.slots.infant.cost.after,
    payable_at_property: plan.payableAtProperty,
    room_rate_id: plan.roomRateId,
    teenager_amount: plan.slots.teenager.sell.after,
    teenager_cost: plan.slots.teenager.cost.after,
  };
}
